import { Injectable, inject, signal, computed, DestroyRef } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { Router, NavigationEnd } from '@angular/router';
import { filter } from 'rxjs/operators';
import { AppConfigService } from './app-config.service';
import { CropMapUserEnterpiseListViewModel } from './user.service';
import { environment } from '@env/environment';

interface AnalyticsState {
    mixpanel: { initialized: boolean };
    googleAnalytics: { initialized: boolean; measurementId: string | null };
    userId: string | null;
    sessionStart: number;
}

declare global {
    interface Window {
        gtag: (command: string, ...args: any[]) => void;
        dataLayer: any[];
    }
}

@Injectable({
    providedIn: 'root',
})
export class UnifiedAnalyticsService {
    private readonly router = inject(Router);
    private readonly appConfigService = inject(AppConfigService);
    private readonly destroyRef = inject(DestroyRef);

    private readonly _state = signal<AnalyticsState>({
        mixpanel: { initialized: false },
        googleAnalytics: { initialized: false, measurementId: null },
        userId: null,
        sessionStart: Date.now(),
    });

    readonly initialized = computed(
        () => this._state().mixpanel.initialized || this._state().googleAnalytics.initialized
    );

    constructor() {
        this.initializeWhenReady();
    }

    private async initializeWhenReady(): Promise<void> {
        try {
            await this.waitForAppConfig();
            await Promise.allSettled([this.initializeMixpanel(), this.initializeGoogleAnalytics()]);

            if (this.initialized()) {
                this.startAutoTracking();
            }
        } catch (error) {
            console.warn('Analytics: Failed to initialize:', error);
        }
    }

    private async initializeMixpanel(): Promise<void> {
        try {
            const projectToken = this.getProjectTokenFromConfig();
            if (!projectToken) {
                console.log('Mixpanel: Disabled (no token or wrong environment)');
                return;
            }

            // TODO: Uncomment when mixpanel-browser is installed
            // import mixpanel from 'mixpanel-browser';
            // mixpanel.init(projectToken, {
            //     debug: !environment.production,
            //     track_pageview: false,
            //     persistence: 'localStorage',
            //     ignore_dnt: false,
            //     api_host: 'https://api-eu.mixpanel.com',
            // });

            this._state.update((s) => ({
                ...s,
                mixpanel: { initialized: false }, // Set to true when mixpanel is installed
            }));

            console.log('Mixpanel: Skipped (mixpanel-browser not installed)');
        } catch (error) {
            console.warn('Mixpanel: Failed to initialize:', error);
        }
    }

    private async initializeGoogleAnalytics(): Promise<void> {
        try {
            const gaConfig = this.getGoogleAnalyticsConfigFromConfig();
            if (!gaConfig) {
                console.log('Google Analytics: Disabled (no measurement ID)');
                return;
            }

            if (!window.dataLayer) {
                window.dataLayer = [];
            }

            window.gtag = function () {
                window.dataLayer.push(arguments);
            };

            window.gtag('js', new Date());

            await this.loadGoogleAnalyticsScript(gaConfig);

            window.gtag('config', gaConfig, {
                page_title: document.title,
                page_location: window.location.href,
                send_page_view: false,
            });

            this._state.update((s) => ({
                ...s,
                googleAnalytics: { initialized: true, measurementId: gaConfig },
            }));

            console.log('Google Analytics: Initialized successfully');
        } catch (error) {
            console.warn('Google Analytics: Failed to initialize:', error);
        }
    }

    private loadGoogleAnalyticsScript(measurementId: string): Promise<void> {
        return new Promise((resolve, reject) => {
            const existingScript = document.querySelector(
                `script[src*="googletagmanager.com/gtag/js?id=${measurementId}"]`
            );
            if (existingScript) {
                resolve();
                return;
            }

            const script = document.createElement('script');
            script.async = true;
            script.src = `https://www.googletagmanager.com/gtag/js?id=${measurementId}`;

            script.onload = () => resolve();
            script.onerror = () => reject(new Error('Failed to load Google Analytics script'));

            document.head.appendChild(script);
        });
    }

    private async waitForAppConfig(): Promise<void> {
        for (let i = 0; i < 50; i++) {
            try {
                const hasConfig =
                    this.appConfigService.mixpanelProjectToken &&
                    this.appConfigService.googleAnalyticsConfig.measurementId;
                if (hasConfig) return;
            } catch {
                // Config not ready yet
            }
            await new Promise((resolve) => setTimeout(resolve, 100));
        }
        throw new Error('App configuration timeout');
    }

    private getProjectTokenFromConfig(): string | null {
        try {
            return this.appConfigService.mixpanelProjectToken || null;
        } catch {
            return null;
        }
    }

    private getGoogleAnalyticsConfigFromConfig(): string | null {
        try {
            return this.appConfigService.googleAnalyticsConfig.measurementId || null;
        } catch {
            return null;
        }
    }

    private startAutoTracking(): void {
        if (!this.initialized()) return;

        this.trackEvent('Session Started', {
            environment: this.detectEnvironmentName(),
            timestamp: new Date().toISOString(),
            user_agent: navigator.userAgent,
            screen_resolution: `${screen.width}x${screen.height}`,
            language: navigator.language,
        });

        this.router.events
            .pipe(
                filter((event) => event instanceof NavigationEnd),
                takeUntilDestroyed(this.destroyRef)
            )
            .subscribe((event) => {
                const navigationEvent = event as NavigationEnd;
                this.trackPageVisit(navigationEvent.urlAfterRedirects || navigationEvent.url);
            });

        window.addEventListener('beforeunload', () => {
            this.trackSessionEnd();
        });
    }

    private trackPageVisit(url: string): void {
        if (!this.initialized()) return;

        const routeInfo = this.categorizeRoute(url);
        const eventData = {
            page_url: url,
            page_name: routeInfo.name,
            page_category: routeInfo.category,
            page_section: routeInfo.section,
            page_depth: this.getRouteDepth(url),
            page_type: this.detectPageType(url),
            timestamp: new Date().toISOString(),
            session_duration: this.getSessionDuration(),
            referrer: document.referrer || 'direct',
        };

        if (this._state().googleAnalytics.initialized && window.gtag) {
            const cleanUrl = url.split('?')[0];
            window.gtag('config', this._state().googleAnalytics.measurementId!, {
                page_title: routeInfo.name || document.title,
                page_location: window.location.origin + cleanUrl,
            });

            window.gtag('event', 'page_view', {
                page_category: routeInfo.category,
                page_section: routeInfo.section,
                page_type: this.detectPageType(url),
            });
        }
    }

    private categorizeRoute(url: string): { name: string; category: string; section: string } {
        const pathSegments = url.split('/').filter((segment) => segment.length > 0);

        if (pathSegments.length === 0) {
            return { name: 'Home', category: 'landing', section: 'main' };
        }

        const firstSegment = pathSegments[0];
        const category = this.inferCategoryFromSegment(firstSegment);
        const section = pathSegments[1] || 'main';
        const name = this.generatePageName(pathSegments);

        return { name, category, section };
    }

    private inferCategoryFromSegment(segment: string): string {
        const categoryMap: Record<string, string> = {
            auth: 'authentication',
            login: 'authentication',
            register: 'authentication',
            account: 'user_management',
            profile: 'user_management',
            settings: 'user_management',
            lands: 'property_management',
            plantations: 'property_management',
            properties: 'property_management',
            services: 'business_services',
            weather: 'business_services',
            soil: 'business_services',
            pest: 'business_services',
            support: 'customer_support',
            help: 'customer_support',
            contact: 'customer_support',
            notifications: 'user_engagement',
            dashboard: 'analytics',
            reports: 'analytics',
        };

        return categoryMap[segment.toLowerCase()] || 'general';
    }

    private generatePageName(segments: string[]): string {
        return segments.map((segment) => this.humanizeSegment(segment)).join(' - ');
    }

    private humanizeSegment(segment: string): string {
        return segment
            .replace(/-/g, ' ')
            .replace(/_/g, ' ')
            .split(' ')
            .map((word) => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
            .join(' ');
    }

    private getRouteDepth(url: string): number {
        return url.split('/').filter((segment) => segment.length > 0).length;
    }

    private detectPageType(url: string): string {
        if (url.includes('/edit') || url.includes('/update')) return 'edit';
        if (url.includes('/create') || url.includes('/new')) return 'create';
        if (url.includes('/list') || url.includes('/browse')) return 'list';
        if (url.includes('/detail') || url.match(/\/\d+$/)) return 'detail';
        if (url.includes('/dashboard')) return 'dashboard';
        if (url.includes('/settings')) return 'settings';
        return 'page';
    }

    private detectEnvironmentName(): string {
        if (environment.apiBaseUrl.toLowerCase().includes('api.doktar.io')) return 'production';
        if (environment.apiBaseUrl.toLowerCase().includes('api.doktar.dev')) return 'development';
        if (environment.apiBaseUrl.toLowerCase().includes('api.doktar.cc')) return 'test';
        return 'local';
    }

    private getSessionDuration(): number {
        return Math.floor((Date.now() - this._state().sessionStart) / 1000);
    }

    identifyUser(user: CropMapUserEnterpiseListViewModel): void {
        if (!this.initialized() || !user.userId) return;

        if (this._state().googleAnalytics.initialized && window.gtag) {
            window.gtag('config', this._state().googleAnalytics.measurementId!, {
                user_id: user.userId,
            });
            window.gtag('event', 'login', { method: 'doktar_login' });
        }

        this._state.update((s) => ({ ...s, userId: user.userId || null }));

        this.track('User Signed In', {
            user_id: user.userId,
            session_duration: this.getSessionDuration(),
        });
    }

    userSignedOut(): void {
        if (!this.initialized()) return;

        this.track('User Signed Out', {
            user_id: this._state().userId,
            session_duration: this.getSessionDuration(),
        });

        if (this._state().googleAnalytics.initialized && window.gtag) {
            window.gtag('config', this._state().googleAnalytics.measurementId!, {
                user_id: null,
            });
        }

        this._state.update((s) => ({ ...s, userId: null, sessionStart: Date.now() }));
    }

    private trackSessionEnd(): void {
        if (!this.initialized()) return;

        this.trackEvent('Session Ended', {
            session_duration: this.getSessionDuration(),
            user_id: this._state().userId,
        });
    }

    private trackEvent(eventName: string, properties: Record<string, any> = {}): void {
        // TODO: Add Mixpanel tracking when mixpanel-browser is installed
    }

    track(eventName: string, properties: Record<string, any> = {}): void {
        if (this._state().googleAnalytics.initialized && window.gtag) {
            const gaEventName = this.sanitizeGAEventName(eventName);
            const gaProperties = this.sanitizeGAProperties(properties);
            window.gtag('event', gaEventName, gaProperties);
        }
    }

    private sanitizeGAEventName(eventName: string): string {
        return eventName
            .toLowerCase()
            .replace(/[^a-z0-9_]/g, '_')
            .replace(/_+/g, '_')
            .replace(/^_|_$/g, '');
    }

    private sanitizeGAProperties(properties: Record<string, any>): Record<string, any> {
        const sanitized: Record<string, any> = {};

        Object.entries(properties).forEach(([key, value]) => {
            const sanitizedKey = key
                .toLowerCase()
                .replace(/[^a-z0-9_]/g, '_')
                .replace(/_+/g, '_')
                .replace(/^_|_$/g, '');

            if (typeof value === 'string' || typeof value === 'number' || typeof value === 'boolean') {
                sanitized[sanitizedKey] = value;
            } else if (value !== null && value !== undefined) {
                sanitized[sanitizedKey] = String(value);
            }
        });

        return sanitized;
    }

    getStatus(): any {
        const state = this._state();
        return {
            mixpanel: { initialized: state.mixpanel.initialized },
            googleAnalytics: {
                initialized: state.googleAnalytics.initialized,
                measurementId: state.googleAnalytics.measurementId,
            },
            user: { hasUser: !!state.userId, userId: state.userId },
            session: { sessionStart: state.sessionStart, sessionDuration: this.getSessionDuration() },
        };
    }
}
